import { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faCheck,
  faCopy,
  faPaperPlane,
} from '@fortawesome/pro-regular-svg-icons';
import type { EmailDraftAction } from '../../types';
import styles from './ActionCard.module.css';
import useNotifications from '../../../../_lib/notifications/useNotifications';

interface Props {
  action: EmailDraftAction;
  onSendMessage?: (message: string, skill?: string) => void;
  isLastMessage?: boolean;
}

export default function EmailDraftCard({
  action,
  onSendMessage,
  isLastMessage,
}: Props) {
  const notifications = useNotifications();
  const [copied, setCopied] = useState(false);

  const subject = action.replyToSubject
    ? `Re: ${action.replyToSubject}`
    : action.title || '';

  const handleCopy = useCallback(() => {
    const lines: string[] = [];
    if (subject) lines.push(`Subject: ${subject}`);
    if (action.toName || action.toEmail) {
      lines.push(
        `To: ${action.toName ?? ''}${
          action.toEmail ? ` <${action.toEmail}>` : ''
        }`
      );
    }
    if (action.cc) lines.push(`Cc: ${action.cc}`);
    if (action.bcc) lines.push(`Bcc: ${action.bcc}`);
    lines.push('');
    lines.push(action.body);

    navigator.clipboard
      .writeText(lines.join('\n'))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Clipboard write failed', err);
        notifications.error('Could not copy to clipboard.');
      });
  }, [action, subject, notifications]);

  return (
    <div className={styles.emailCard}>
      <div className={styles.emailHeader}>
        <div className={styles.emailHeaderLeft}>
          <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 14 }} />
          Email Draft
        </div>
        <div className={styles.emailHeaderActions}>
          <button
            className={copied ? styles.emailCopied : styles.emailCopyButton}
            onClick={handleCopy}
            type="button"
          >
            {copied ? (
              <>
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />
                Copied
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCopy} style={{ fontSize: 12 }} />
                Copy
              </>
            )}
          </button>
          <button
            className={
              isLastMessage
                ? styles.emailSendButton
                : styles.emailSendButtonDisabled
            }
            onClick={() => isLastMessage && onSendMessage?.('Send Email')}
            type="button"
            disabled={!isLastMessage}
          >
            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 12 }} />
            Send
          </button>
        </div>
      </div>

      {subject && (
        <div className={styles.emailField}>
          <span className={styles.emailFieldLabel}>Subject:</span>
          <span className={styles.emailFieldValue}>{subject}</span>
        </div>
      )}
      {(action.toName || action.toEmail) && (
        <div className={styles.emailField}>
          <span className={styles.emailFieldLabel}>To:</span>
          <span className={styles.emailFieldValue}>
            {action.toName}
            {action.toEmail ? ` <${action.toEmail}>` : ''}
          </span>
        </div>
      )}
      {action.cc && (
        <div className={styles.emailField}>
          <span className={styles.emailFieldLabel}>Cc:</span>
          <span className={styles.emailFieldValue}>{action.cc}</span>
        </div>
      )}
      {action.bcc && (
        <div className={styles.emailField}>
          <span className={styles.emailFieldLabel}>Bcc:</span>
          <span className={styles.emailFieldValue}>{action.bcc}</span>
        </div>
      )}

      <hr className={styles.emailDivider} />

      <div className={styles.emailBody}>{action.body}</div>
    </div>
  );
}
